import React, { useEffect, useState } from 'react';
import { supportApi } from '../api/api';
import { handleApiError } from '@/shared/api/requests';
import type { BugReport } from '../types/types';
import { StatusBadge } from './StatusBadge';

const BugReportList: React.FC = () => {
  const [reports, setReports] = useState<BugReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  // Map of report uuid -> signed GET URLs (fetched on expand)
  const [signedImages, setSignedImages] = useState<Record<string, string[]>>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const data = await supportApi.getMyReports();
        setReports(data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, []);

  const toggleExpand = async (report: BugReport) => {
    const isCurrentlyExpanded = expandedIds.has(report.id);

    setExpandedIds((prev) => {
      const next = new Set(prev);
      isCurrentlyExpanded ? next.delete(report.id) : next.add(report.id);
      return next;
    });

    // Fetch signed URLs on first expand if report has images
    if (!isCurrentlyExpanded && report.images.length > 0 && !signedImages[report.uuid]) {
      setLoadingImages((prev) => new Set(prev).add(report.uuid));
      try {
        const urls = await supportApi.getImageSignedUrls(report.uuid);
        setSignedImages((prev) => ({ ...prev, [report.uuid]: urls }));
      } catch {
        // Silently fail — thumbnails will just not appear
      } finally {
        setLoadingImages((prev) => {
          const next = new Set(prev);
          next.delete(report.uuid);
          return next;
        });
      }
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading your reports...</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (reports.length === 0) {
    return <p className="text-sm text-gray-500">You haven't submitted any bug reports yet.</p>;
  }

  return (
    <div className="space-y-3">
      {reports.map((report) => {
        const isExpanded = expandedIds.has(report.id);
        const imageUrls = signedImages[report.uuid] ?? [];
        const imagesLoading = loadingImages.has(report.uuid);

        return (
          <div
            key={report.id}
            className="rounded-lg border border-gray-200 bg-white overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggleExpand(report)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <StatusBadge status={report.status} />
                <span className="text-sm font-medium text-gray-900 truncate">{report.subject}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                <span className="text-xs text-gray-400">{formatDate(report.created_at)}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z" />
                </svg>
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100 space-y-3 pt-3">
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Your Report</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{report.description}</p>
                </div>

                {report.images.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Screenshots</p>
                    {imagesLoading ? (
                      <p className="text-xs text-gray-400">Loading screenshots...</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {imageUrls.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity flex-shrink-0"
                          >
                            <img src={url} alt={`screenshot ${i + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {report.staff_response && (
                  <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-3">
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Staff Response</p>
                    <p className="text-sm text-blue-900 whitespace-pre-wrap">{report.staff_response}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export { BugReportList };
